/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = 'https://coast-terminal-production.up.railway.app';

interface Article {
  id: number;
  title: string;
  summary: string;
  url: string;
  source: string;
  published_at: string;
  category: string;
  msas: string[];
  relevance_score: number;
  is_featured: boolean;
  sentiment: string;
}

interface NewsFeedProps {
  token: string;
  msa?: string;
}

const CATEGORY_LABELS: { [key: string]: { label: string; color: string } } = {
  rate_environment: { label: 'Rates',       color: '#1A5A8A' },
  supply_demand:    { label: 'Supply',      color: '#1A7A4A' },
  transaction:      { label: 'Transaction', color: '#b8956a' },
  distress:         { label: 'Distress',    color: '#CC2200' },
  policy:           { label: 'Policy',      color: '#7A5A9A' },
  macro:            { label: 'Macro',       color: '#4A7A9A' },
  market_trend:     { label: 'Trend',       color: '#4A9A7A' },
  general:          { label: 'General',     color: '#2d7fa0' },
};

const ALL_CATS = ['all', ...Object.keys(CATEGORY_LABELS)];
const POLL_INTERVAL_MS = 2 * 60 * 1000;

const NewsFeed: React.FC<NewsFeedProps> = ({ token, msa }) => {
  const gold = '#b8956a';
  const surface = '#fafaf8';
  const surfaceRaised = '#f2ede6';
  const border = '#e6ddd4';
  const textPrimary = '#0d3d5c';
  const textSecondary = '#12526e';
  const textMuted = '#2d7fa0';
  const serifFont = "'Fraunces', Georgia, serif";
  const headers = { Authorization: `Bearer ${token}` };

  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [total, setTotal] = useState(0);
  const [lastRefresh, setLastRefresh] = useState('');
  const [catCounts, setCatCounts] = useState<{ [key: string]: number }>({});

  const fetchNews = useCallback(async (cat: string) => {
    try {
      const params: { [key: string]: any } = { limit: 40 };
      if (msa) params.msa = msa;
      if (cat && cat !== 'all') params.category = cat;
      const res = await axios.get(`${API}/terminal/news/`, { headers, params });
      setArticles(res.data.articles || []);
      setTotal(res.data.total || 0);
      setLastRefresh(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [msa]); // eslint-disable-line

  const fetchCounts = useCallback(async () => {
    try {
      const params: { [key: string]: any } = {};
      if (msa) params.msa = msa;
      const res = await axios.get(`${API}/terminal/news/categories`, { headers, params });
      const m: { [key: string]: number } = { all: 0 };
      for (const item of (res.data.categories || [])) {
        m[item.category] = item.count;
        m['all'] = (m['all'] || 0) + item.count;
      }
      setCatCounts(m);
    } catch {
      // silent
    }
  }, [msa]); // eslint-disable-line

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  useEffect(() => {
    setLoading(true);
    fetchNews(selectedCategory);
  }, [msa, selectedCategory, fetchNews]);

  useEffect(() => {
    const refreshFeed = () => {
      if (document.visibilityState !== 'visible' || !navigator.onLine) return;
      fetchNews(selectedCategory);
      fetchCounts();
    };

    const t = setInterval(refreshFeed, POLL_INTERVAL_MS);
    window.addEventListener('focus', refreshFeed);
    document.addEventListener('visibilitychange', refreshFeed);

    return () => {
      clearInterval(t);
      window.removeEventListener('focus', refreshFeed);
      document.removeEventListener('visibilitychange', refreshFeed);
    };
  }, [selectedCategory, fetchNews, fetchCounts]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/terminal/news/refresh`, {}, { headers });
      await fetchNews(selectedCategory);
      await fetchCounts();
    } catch {
      // silent
    } finally {
      setRefreshing(false);
    }
  };

  const relTime = (d: string) => {
    const diff = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
    if (diff < 1) return 'just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  };

  const sentimentColor = (s: string) =>
    s === 'bullish' ? '#1A7A4A' : s === 'bearish' ? '#CC2200' : '#2d7fa0';

  const sentimentIcon = (s: string) =>
    s === 'bullish' ? '▲' : s === 'bearish' ? '▼' : '—';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Header */}
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${border}`, backgroundColor: '#fafaf8', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#1A7A4A', animation: 'pulse 2.5s ease-in-out infinite' }} />
          <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '2px', fontWeight: 700, textTransform: 'uppercase' }}>
            Live Feed {msa ? `· ${msa.split(',')[0]}` : '· All Markets'}
          </div>
          {total > 0 && <div style={{ fontSize: '9px', color: textMuted }}>{total}</div>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {lastRefresh && <div style={{ fontSize: '9px', color: textMuted }}>{lastRefresh}</div>}
          <button onClick={handleRefresh} disabled={refreshing}
            style={{ backgroundColor: 'transparent', border: `1px solid ${border}`, color: refreshing ? textMuted : textSecondary, padding: '4px 10px', fontSize: '9px', fontWeight: 700, cursor: refreshing ? 'default' : 'pointer', fontFamily: 'inherit', letterSpacing: '1px', textTransform: 'uppercase' }}>
            {refreshing ? '...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Category tabs */}
      <div style={{ display: 'flex', overflowX: 'auto', borderBottom: `1px solid ${border}`, flexShrink: 0 }}>
        {ALL_CATS.map(cat => {
          const n = cat === 'all' ? (catCounts['all'] || total) : (catCounts[cat] || 0);
          if (cat !== 'all' && n === 0) return null;
          const cfg = CATEGORY_LABELS[cat];
          const active = selectedCategory === cat;
          const tabColor = cfg ? cfg.color : gold;
          return (
            <button key={cat} onClick={() => setSelectedCategory(cat)}
              style={{ padding: '8px 12px', border: 'none', borderBottom: `2px solid ${active ? tabColor : 'transparent'}`, cursor: 'pointer', fontSize: '9px', fontWeight: active ? 700 : 500, letterSpacing: '1px', textTransform: 'uppercase', fontFamily: 'inherit', backgroundColor: 'transparent', color: active ? tabColor : textMuted, whiteSpace: 'nowrap', flexShrink: 0 }}>
              {cfg ? cfg.label : 'All'}
              {n > 0 && <span style={{ marginLeft: '4px', fontSize: '8px', opacity: 0.6 }}>({n})</span>}
            </button>
          );
        })}
      </div>

      {/* Articles */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ padding: '24px 16px' }}>
            {[1,2,3,4,5].map(i => (
              <div key={i} style={{ marginBottom: '16px', padding: '14px', backgroundColor: '#ffffff', border: `1px solid ${border}` }}>
                <div style={{ height: '12px', width: '60%', backgroundColor: '#fafaf8', marginBottom: '8px', animation: 'pulse 1.5s ease-in-out infinite' }} />
                <div style={{ height: '10px', width: '90%', backgroundColor: '#fafaf8', marginBottom: '6px', animation: 'pulse 1.5s ease-in-out infinite' }} />
                <div style={{ height: '10px', width: '40%', backgroundColor: '#fafaf8', animation: 'pulse 1.5s ease-in-out infinite' }} />
              </div>
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div style={{ padding: '32px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: '13px', color: textMuted, fontFamily: serifFont, marginBottom: '8px' }}>No recent coverage</div>
            <div style={{ fontSize: '11px', color: textMuted, lineHeight: 1.6 }}>
              {selectedCategory === 'distress'
                ? 'Distress events are infrequent — populates when defaults or foreclosures are reported.'
                : selectedCategory === 'market_trend'
                ? 'Trend reports publish quarterly. Check back after Q1/Q2/Q3/Q4 cycles.'
                : selectedCategory === 'macro'
                ? 'Macro coverage appears when Fed decisions, GDP, or jobs data are published.'
                : 'No articles found. Try All or Refresh.'}
            </div>
          </div>
        ) : (
          <div>
            {articles.map((article) => {
              const cfg = CATEGORY_LABELS[article.category] || CATEGORY_LABELS['general'];
              return (
                <a key={article.id} href={article.url} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'block', textDecoration: 'none', borderBottom: `1px solid ${border}`, padding: '12px 16px', transition: 'background 0.1s' }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = surfaceRaised)}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {article.is_featured && <div style={{ width: '2px', flexShrink: 0, alignSelf: 'stretch', backgroundColor: cfg.color }} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '8px', fontWeight: 800, color: cfg.color, letterSpacing: '1px', textTransform: 'uppercase' }}>{cfg.label}</span>
                        <span style={{ fontSize: '8px', fontWeight: 800, color: sentimentColor(article.sentiment) }}>{sentimentIcon(article.sentiment)}</span>
                        {article.msas.slice(0, 2).map(m => (
                          <span key={m} style={{ fontSize: '8px', color: textMuted, backgroundColor: '#fafaf8', padding: '1px 5px', border: `1px solid ${border}` }}>{m.split(',')[0]}</span>
                        ))}
                        {article.is_featured && <span style={{ fontSize: '8px', color: gold }}>★</span>}
                      </div>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: textPrimary, lineHeight: 1.4, marginBottom: '4px', fontFamily: serifFont }}>{article.title}</div>
                      {article.summary && (
                        <div style={{ fontSize: '11px', color: textSecondary, lineHeight: 1.5, marginBottom: '6px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{article.summary}</div>
                      )}
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={{ fontSize: '9px', color: gold, fontWeight: 700 }}>{article.source}</span>
                        <span style={{ fontSize: '9px', color: textMuted }}>·</span>
                        <span style={{ fontSize: '9px', color: textMuted }}>{relTime(article.published_at)}</span>
                      </div>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>
    </div>
  );
};

export default NewsFeed;
